/**
 * Neyra — Analytics & activity helpers.
 *
 * Boost-style analytics for listings: views, clicks, bookings. Updated by
 * UI interactions (record*) and read in Insights / boosted chips.
 *
 * Also exposes a computed activity feed by combining bookings, joined events,
 * and new listings from existing stores — no duplicate state.
 */

import { getBookingsByBuyer } from "@/lib/bookingStore";
import { getJoinedEvents } from "@/lib/socialStore";
import { getAllEvents, listServicesByOwner, listEventsByOwner } from "@/lib/entitiesStore";

const BUS_EVT = "neyra-analytics-change";
const emit = () => window.dispatchEvent(new Event(BUS_EVT));

export const subscribeAnalytics = (cb) => {
    window.addEventListener(BUS_EVT, cb);
    return () => window.removeEventListener(BUS_EVT, cb);
};

const read = (k, fallback) => {
    try {
        const v = localStorage.getItem(k);
        return v ? JSON.parse(v) : fallback;
    } catch { return fallback; }
};
const write = (k, v) => { localStorage.setItem(k, JSON.stringify(v)); emit(); };

/** Listing-level counters keyed by kind+id globally. */
const KEY = "neyra.analytics.counters";

const getCounters = () => read(KEY, {});

const counterKey = (kind, id) => `${kind}:${id}`;

const bump = (kind, id, field) => {
    if (!id) return;
    const all = getCounters();
    const k = counterKey(kind, id);
    const cur = all[k] || { views: 0, clicks: 0, bookings: 0 };
    cur[field] = (cur[field] || 0) + 1;
    all[k] = cur;
    write(KEY, all);
};

export const recordView = (kind, id) => bump(kind, id, "views");
export const recordClick = (kind, id) => bump(kind, id, "clicks");
export const recordBooking = (kind, id) => bump(kind, id, "bookings");

export const getListingStats = (kind, id) => {
    const all = getCounters();
    return all[counterKey(kind, id)] || { views: 0, clicks: 0, bookings: 0 };
};

/** Aggregate stats for a seller across all their listings. */
export const getOwnerStats = (userId) => {
    const services = listServicesByOwner(userId) || [];
    const events = listEventsByOwner(userId) || [];
    let views = 0, clicks = 0, bookings = 0;
    for (const s of services) {
        const st = getListingStats("service", s.id);
        views += st.views; clicks += st.clicks; bookings += st.bookings;
    }
    for (const e of events) {
        const st = getListingStats("event", e.id);
        views += st.views; clicks += st.clicks;
    }
    return {
        views,
        clicks,
        bookings,
        services: services.length,
        events: events.length,
        // Conversion = bookings / views (bounded)
        conversion: views > 0 ? Math.round((bookings / views) * 1000) / 10 : 0,
    };
};

/* ---------------- Activity feed (computed) ---------------- */

export const buildActivityFeed = (userId) => {
    if (!userId) return [];
    const items = [];

    // Booked services
    try {
        const bookings = getBookingsByBuyer(userId);
        for (const b of bookings) {
            items.push({
                id: "act_bk_" + b.id,
                kind: "booking",
                at: b.createdAt,
                title: `Booked ${b.serviceName}`,
                subtitle: `${b.date} · ${b.time} · ${b.mode === "virtual" ? "Virtual" : b.location || "In-person"}`,
                link: `/bookings/${b.id}`,
                meta: b.status,
            });
        }
    } catch {}

    // Joined events
    try {
        const joinedIds = getJoinedEvents(userId) || [];
        const allEvents = getAllEvents() || [];
        for (const eid of joinedIds) {
            const e = allEvents.find((x) => x.id === eid);
            if (!e) continue;
            items.push({
                id: "act_ev_" + eid,
                kind: "event",
                at: e.startAt || e.createdAt || Date.now() - 86_400_000,
                title: `Joined ${e.title || e.name || "event"}`,
                subtitle: e.location || "",
                link: `/events/${eid}`,
            });
        }
    } catch {}

    // User's own new listings
    try {
        const services = listServicesByOwner(userId) || [];
        for (const s of services) {
            items.push({
                id: "act_sv_" + s.id,
                kind: "listing",
                at: s.createdAt || Date.now(),
                title: `Listed ${s.name}`,
                subtitle: s.category || "Service",
                link: `/services/${s.id}`,
            });
        }
        const events = listEventsByOwner(userId) || [];
        for (const e of events) {
            items.push({
                id: "act_evown_" + e.id,
                kind: "listing",
                at: e.createdAt || Date.now(),
                title: `Hosted ${e.title || e.name}`,
                subtitle: e.location || "Event",
                link: `/events/${e.id}`,
            });
        }
    } catch {}

    return items.sort((a, b) => (b.at || 0) - (a.at || 0)).slice(0, 50);
};
